import { useCallback, useEffect, useMemo } from "react";

import { PalButton, PalCheckbox, PalTextField } from "@palamar/fe-library";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { MdFileCopy } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { Grid } from "@mui/material";

import useProjectChange from "~common/hooks/useProjectChange";
import PRContainer from "~components/Generic/PRContainer";
import { PRTextArea } from "~components/Generic/PRInput";
import PRPage from "~components/Generic/PRPage";
import DialogHelper from "~helpers/DialogHelper";
import HistoryHelper from "~helpers/HistoryHelper";
import { getCurrentUser } from "~store/actions";
import {
  copyStatisticPage,
  createOrUpdateStatisticPage,
  getStatisticPage,
  setStatisticPage,
} from "~store/statisticPages/actions";
import { selectStatisticPage } from "~store/statisticPages/selectors";
import { selectCurrentProject, selectUserInfo } from "~store/user/selectors";

const StatisticPagesEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const dispatch = useDispatch();

  const currentProject = useSelector(selectCurrentProject);
  const statisticPage = useSelector(selectStatisticPage);

  const userInfo = useSelector(selectUserInfo);

  const handleClickCancel = () => {
    HistoryHelper.goBack("/stats/settings/statistic-pages/", { scope: "dashboard" });
  };

  useEffect(() => {
    if (!id) return;
    dispatch(getStatisticPage(currentProject.id, id));

    return () => {
      dispatch(setStatisticPage(null));
    };
    // Page should be redirected to list if selected project is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: id
      ? {
          id: statisticPage?.id,
          name: statisticPage?.name || "",
          is_menu: statisticPage?.is_menu ?? false,
          is_default: statisticPage?.is_default ?? false,
          is_active: statisticPage?.is_active ?? (id ? false : true),
          page_data: JSON.stringify(statisticPage?.page_data || [], null, 2),
        }
      : {
          name: "",
          is_menu: false,
          is_default: false,
          is_active: true,
          page_data: "[]",
        },
    validationSchema: Yup.object({
      name: Yup.string().required(t("component.formik.required").format(t("common.name"))),
      is_menu: Yup.boolean().required(t("common.required")),
      is_default: Yup.boolean().required(t("common.required")),
      is_active: Yup.boolean().required(t("common.required")),
      page_data: Yup.string()
        .required(t("component.formik.required").format(t("dashboard.statisticPages.pageData")))
        .test("is-json", t("component.formik.invalid").format(t("dashboard.statisticPages.pageData")), (value) => {
          try {
            JSON.parse(value);
            if (!Array.isArray(JSON.parse(value))) {
              return false;
            }
            return true;
          } catch (e) {
            return false;
          }
        }),
    }),
    onSubmit: async (values) => {
      await dispatch(
        createOrUpdateStatisticPage(currentProject.id, {
          ...values,
          page_data: JSON.parse(values.page_data || []),
        })
      );
      await dispatch(getCurrentUser());
      handleClickCancel();
    },
  });

  useProjectChange(() => {
    HistoryHelper.push("/stats/settings/statistic-pages/", { scope: "dashboard" });
  }, []);

  const parentName = useMemo(
    () => [
      {
        label: t("dashboard.statisticPages"),
        url: "/stats/settings/statistic-pages/",
      },
      {
        label: id ? t("dashboard.statisticPages.edit") : t("dashboard.statisticPages.create"),
      },
    ],
    [id, t]
  );

  const handleCopyPage = useCallback(async () => {
    if (
      !(await DialogHelper.showQuestionYesNo(
        t("dashboard.statisticPagesEdit.copyConfirmTitle"),
        t("dashboard.statisticPagesEdit.copyConfirm")
      ))
    ) {
      return;
    }
    const response = await dispatch(copyStatisticPage(currentProject?.id, id));
    HistoryHelper.push(`/stats/settings/statistic-pages/${response.id}/`, { scope: "dashboard" });
  }, [dispatch, id, currentProject?.id, t]);

  const actions = useMemo(() => {
    return id
      ? [
          {
            label: t("dashboard.statisticPagesEdit.copy"),
            icon: MdFileCopy,
            color: "primary",
            onClick: handleCopyPage,
          },
        ]
      : [];
  }, [id, t, handleCopyPage]);

  return (
    <PRContainer
      actions={actions}
      description={t("dashboard.statisticPagesEdit.description")}
      name={t("common.statistics")}
      parentName={parentName}
    >
      <PRPage>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PalTextField
              fullWidth
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              label={t("common.name")}
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <PRTextArea
              editorMode
              editorProps={{
                defaultLanguage: "json",
                defaultHeight: 300,
              }}
              invalid={formik.touched.page_data && formik.errors.page_data}
              name="page_data"
              placeholder={"[]"}
              value={formik.values.page_data}
              onChange={formik.handleChange}
            />
          </Grid>
          {userInfo?.is_superuser && (
            <Grid item xs={12}>
              <PalCheckbox
                checked={formik.values.is_active}
                error={formik.touched.is_active && Boolean(formik.errors.is_active)}
                helperText={formik.touched.is_active && formik.errors.is_active}
                name="is_active"
                onChange={formik.handleChange}
              />
              {t("dashboard.statisticPagesEdit.isActive")}
            </Grid>
          )}
          {/* Handle Submit */}
          <Grid item className="d-flex justify-content-end" xs={12}>
            <PalButton
              color="primary"
              disabled={formik.isSubmitting || !formik.isValid}
              loading={formik.isSubmitting}
              onClick={formik.handleSubmit}
            >
              {t("common.save")}
            </PalButton>
            <PalButton color="secondary" disabled={formik.isSubmitting} onClick={handleClickCancel}>
              {t("common.cancel")}
            </PalButton>
          </Grid>
        </Grid>
      </PRPage>
    </PRContainer>
  );
};

export default StatisticPagesEdit;
